// One-device party games (Dumb Charades, Finish the Dialogue, Mat Bolna): the dealing types and the
// scoring rules as small pure state machines. The server deals the cards a few at a time; the rules run
// in the browser and are unit tested on the server.
import type { CardData } from './types';

export const MINIGAMES = ['charades', 'dialogue', 'mat-bolna'] as const;
export type MinigameName = (typeof MINIGAMES)[number];

export function isMinigame(value: unknown): value is MinigameName {
  return typeof value === 'string' && (MINIGAMES as readonly string[]).includes(value);
}

/** A game's full deck. Server-side only: phones get small batches from a deal session. */
export interface MinigameFeed {
  game: MinigameName;
  cards: CardData[];
}

/** GET /api/minigames/:game/stats: counts only, so the setup screen can say how many cards match. */
export interface MinigameStats {
  game: MinigameName;
  total: number;
  matrix: Record<Category | 'none', Record<Difficulty, number>>;
}

/** POST /api/minigames/:game/deal and POST /api/minigames/deal/:sessionId/more */
export interface DealBatch {
  sessionId?: string;
  total?: number;
  cards: CardData[];
  remaining: number;
}

export function emptyMatrix(): MinigameStats['matrix'] {
  const row = () => ({ easy: 0, medium: 0, hard: 0 });
  return { film: row(), song: row(), tv: row(), none: row() };
}

/** How many cards match the filters, from the counts alone. */
export function countMatching(stats: MinigameStats, opts: { difficulties?: readonly Difficulty[]; categories?: readonly Category[] }): number {
  let n = 0;
  for (const [cat, row] of Object.entries(stats.matrix) as [Category | 'none', Record<Difficulty, number>][]) {
    if (opts.categories?.length && (cat === 'none' || !opts.categories.includes(cat))) continue;
    for (const d of DIFFICULTIES) if (!opts.difficulties?.length || opts.difficulties.includes(d)) n += row[d];
  }
  return n;
}

/** Add freshly dealt cards to a running game's deck. New cards go underneath, so they come up last. */
export function withMoreCards<S extends { deck: Deck }>(s: S, ids: readonly string[]): S {
  const known = new Set([...s.deck.draw, ...s.deck.used]);
  const fresh = ids.filter((id) => !known.has(id));
  if (!fresh.length) return s;
  return { ...s, deck: { draw: [...fresh, ...s.deck.draw], used: s.deck.used } };
}

export const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

export const CATEGORIES = ['film', 'song', 'tv'] as const;
export type Category = (typeof CATEGORIES)[number];

export const MIN_TEAMS = 2;
export const MAX_TEAMS = 4;
export const TEAM_NAME_MAX = 20;

/** Cards without a difficulty tag count as medium. */
export function difficultyOf(card: Pick<CardData, 'tags'>): Difficulty {
  return DIFFICULTIES.find((d) => card.tags?.includes(d)) ?? 'medium';
}

/** The first film/song/tv tag on the card, in the card's own tag order. */
export function categoryOf(card: Pick<CardData, 'tags'>): Category | null {
  return (card.tags?.find((t) => (CATEGORIES as readonly string[]).includes(t)) as Category | undefined) ?? null;
}

/** "DJ / gaana" -> ["DJ", "gaana"]: one word per blank for multi-blank film lines. */
export function originalAnswers(card: Pick<CardData, 'original'>): string[] {
  return (card.original ?? '')
    .split(' / ')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function filterCards(
  cards: CardData[],
  opts: { difficulties?: readonly Difficulty[]; categories?: readonly Category[] },
): CardData[] {
  return cards.filter((c) => {
    if (opts.difficulties?.length && !opts.difficulties.includes(difficultyOf(c))) return false;
    if (opts.categories?.length) {
      const cat = categoryOf(c);
      if (!cat || !opts.categories.includes(cat)) return false;
    }
    return true;
  });
}

export function shuffle<T>(items: readonly T[], random: () => number = Math.random): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Trim, collapse spaces, cap length, and fill in "Team 2" style names for empty ones. */
export function cleanTeamNames(names: readonly string[]): string[] {
  const used = new Set<string>();
  return names.slice(0, MAX_TEAMS).map((raw, i) => {
    let name = String(raw ?? '').replace(/\s+/g, ' ').trim().slice(0, TEAM_NAME_MAX) || `Team ${i + 1}`;
    if (used.has(name.toLowerCase())) name = `${name} ${i + 1}`;
    used.add(name.toLowerCase());
    return name;
  });
}

export interface Team {
  name: string;
  score: number;
}

/** A deck of card ids that reshuffles what was used once it runs dry. */
export interface Deck {
  draw: string[];
  used: string[];
}

function drawCard(deck: Deck, random: () => number, avoid: ReadonlySet<string> = new Set()): string | null {
  if (!deck.draw.length) {
    const reusable = deck.used.filter((id) => !avoid.has(id));
    if (!reusable.length) return null;
    deck.draw = shuffle(reusable, random);
    deck.used = deck.used.filter((id) => avoid.has(id));
  }
  const id = deck.draw.pop()!;
  deck.used.push(id);
  return id;
}

// ------------------------------------------------------------------ turn games (charades, mat bolna)

/** correct: +1. skip: no points. foul: said a forbidden word (Mat Bolna), -1. */
export type Outcome = 'correct' | 'skip' | 'foul';
export const OUTCOME_POINTS: Record<Outcome, number> = { correct: 1, skip: 0, foul: -1 };

export interface TurnGameSettings {
  turnSeconds: number;
  /** How many turns each team gets before the game ends. */
  turnsPerTeam: number;
}

export interface TurnGameState {
  kind: 'turns';
  teams: Team[];
  settings: TurnGameSettings;
  deck: Deck;
  /** Index into teams of the team playing (or about to play). */
  team: number;
  /** Turns completed so far, across all teams. */
  turnsPlayed: number;
  /** ready: waiting for the team to start the clock. playing: clock running. review: time's up. over: game finished. */
  phase: 'ready' | 'playing' | 'review' | 'over';
  cardId: string | null;
  /** Epoch ms when the running turn ends. */
  endsAt: number | null;
  /** This turn's cards so far, in order. */
  results: { cardId: string; outcome: Outcome }[];
}

export function newTurnGame(teamNames: readonly string[], settings: TurnGameSettings, cardIds: readonly string[], random = Math.random): TurnGameState {
  const names = cleanTeamNames(teamNames);
  if (names.length < MIN_TEAMS) throw new Error(`You need at least ${MIN_TEAMS} teams.`);
  if (!cardIds.length) throw new Error('No cards match those filters.');
  return {
    kind: 'turns',
    teams: names.map((name) => ({ name, score: 0 })),
    settings: {
      turnSeconds: Math.max(10, Math.min(600, Math.round(settings.turnSeconds))),
      turnsPerTeam: Math.max(1, Math.min(20, Math.round(settings.turnsPerTeam))),
    },
    deck: { draw: shuffle(cardIds, random), used: [] },
    team: 0,
    turnsPlayed: 0,
    phase: 'ready',
    cardId: null,
    endsAt: null,
    results: [],
  };
}

export function startTurn(s: TurnGameState, now: number, random = Math.random): TurnGameState {
  if (s.phase !== 'ready') return s;
  const next: TurnGameState = { ...s, deck: { draw: [...s.deck.draw], used: [...s.deck.used] }, results: [] };
  next.cardId = drawCard(next.deck, random);
  next.phase = 'playing';
  next.endsAt = now + s.settings.turnSeconds * 1000;
  return next;
}

/** Score the card on screen and deal the next one. */
export function markCard(s: TurnGameState, outcome: Outcome, random = Math.random): TurnGameState {
  if (s.phase !== 'playing' || !s.cardId) return s;
  const next: TurnGameState = {
    ...s,
    deck: { draw: [...s.deck.draw], used: [...s.deck.used] },
    teams: s.teams.map((t, i) => (i === s.team ? { ...t, score: t.score + OUTCOME_POINTS[outcome] } : t)),
    results: [...s.results, { cardId: s.cardId, outcome }],
  };
  // Don't hand this turn's cards straight back if the deck has to reshuffle mid-turn.
  next.cardId = drawCard(next.deck, random, new Set(next.results.map((r) => r.cardId)));
  if (!next.cardId) return endTurn(next);
  return next;
}

/** Time's up (or the team stopped early). The card on screen doesn't count. */
export function endTurn(s: TurnGameState): TurnGameState {
  if (s.phase !== 'playing') return s;
  return { ...s, phase: 'review', endsAt: null, cardId: null };
}

/** Fix a mis-tap on the review screen. */
export function amendResult(s: TurnGameState, index: number, outcome: Outcome): TurnGameState {
  const result = s.results[index];
  if (s.phase !== 'review' || !result || result.outcome === outcome) return s;
  const delta = OUTCOME_POINTS[outcome] - OUTCOME_POINTS[result.outcome];
  return {
    ...s,
    teams: s.teams.map((t, i) => (i === s.team ? { ...t, score: t.score + delta } : t)),
    results: s.results.map((r, i) => (i === index ? { ...r, outcome } : r)),
  };
}

/** Hand over to the next team, or finish the game once every team has had its turns. */
export function nextTurn(s: TurnGameState): TurnGameState {
  if (s.phase !== 'review') return s;
  const turnsPlayed = s.turnsPlayed + 1;
  if (turnsPlayed >= s.teams.length * s.settings.turnsPerTeam) {
    return { ...s, turnsPlayed, phase: 'over', results: [] };
  }
  return { ...s, turnsPlayed, team: (s.team + 1) % s.teams.length, phase: 'ready', results: [] };
}

/** Stop the game now; the scores so far stand. */
export function finishTurnGame(s: TurnGameState): TurnGameState {
  return { ...s, phase: 'over', cardId: null, endsAt: null, results: [] };
}

/** Which round the current team is on, from 1. */
export function roundOf(s: TurnGameState): number {
  return Math.floor(s.turnsPlayed / s.teams.length) + 1;
}

// ------------------------------------------------------------------ quiz (finish the dialogue)

export interface QuizSettings {
  /** Seconds to shout an answer before the line is revealed. 0 turns the timer off. */
  lineSeconds: number;
  /** Lines in the game. */
  lines: number;
}

export interface QuizState {
  kind: 'quiz';
  teams: Team[];
  settings: QuizSettings;
  deck: Deck;
  /** Lines finished so far. */
  played: number;
  /** asking: the line is up with its blank. revealed: the original word is showing. */
  phase: 'asking' | 'revealed' | 'over';
  cardId: string | null;
  endsAt: number | null;
  /** Who won each finished line (null: nobody), newest last. */
  history: { cardId: string; team: number | null }[];
}

export function newQuiz(
  teamNames: readonly string[],
  settings: QuizSettings,
  cardIds: readonly string[],
  now: number,
  random = Math.random,
  /** Lines available on the server, when the deck is dealt a few at a time. */
  available = cardIds.length,
): QuizState {
  const names = cleanTeamNames(teamNames);
  if (names.length < MIN_TEAMS) throw new Error(`You need at least ${MIN_TEAMS} teams.`);
  if (!cardIds.length) throw new Error('No lines to play.');
  const lineSeconds = Math.max(0, Math.min(300, Math.round(settings.lineSeconds)));
  const deck: Deck = { draw: shuffle(cardIds, random), used: [] };
  const cardId = drawCard(deck, random);
  return {
    kind: 'quiz',
    teams: names.map((name) => ({ name, score: 0 })),
    // Never promise more lines than there are cards.
    settings: { lineSeconds, lines: Math.max(1, Math.min(Math.max(available, cardIds.length), Math.round(settings.lines))) },
    deck,
    played: 0,
    phase: 'asking',
    cardId,
    endsAt: lineSeconds ? now + lineSeconds * 1000 : null,
    history: [],
  };
}

export function revealLine(s: QuizState): QuizState {
  if (s.phase !== 'asking') return s;
  return { ...s, phase: 'revealed', endsAt: null };
}

/** Give the line to a team (or to nobody) and move on. Works before or after the reveal. */
export function awardLine(s: QuizState, team: number | null, now: number, random = Math.random): QuizState {
  if (s.phase === 'over' || !s.cardId) return s;
  const valid = team !== null && team >= 0 && team < s.teams.length ? team : null;
  const next: QuizState = {
    ...s,
    teams: s.teams.map((t, i) => (i === valid ? { ...t, score: t.score + 1 } : t)),
    history: [...s.history, { cardId: s.cardId, team: valid }],
    played: s.played + 1,
  };
  return dealLine(next, now, random);
}

/** Skip a line nobody knows. It doesn't count towards the game length. */
export function skipLine(s: QuizState, now: number, random = Math.random): QuizState {
  if (s.phase === 'over' || !s.cardId) return s;
  return dealLine({ ...s }, now, random, s.cardId);
}

function dealLine(s: QuizState, now: number, random: () => number, avoid?: string): QuizState {
  if (s.played >= s.settings.lines) return { ...s, phase: 'over', cardId: null, endsAt: null };
  const deck = { draw: [...s.deck.draw], used: [...s.deck.used] };
  let cardId = drawCard(deck, random, new Set(avoid ? [avoid] : []));
  if (!cardId && avoid) cardId = avoid;
  if (!cardId) return { ...s, deck, phase: 'over', cardId: null, endsAt: null };
  return { ...s, deck, cardId, phase: 'asking', endsAt: s.settings.lineSeconds ? now + s.settings.lineSeconds * 1000 : null };
}

/** Stop the game now; the scores so far stand. */
export function finishQuiz(s: QuizState): QuizState {
  return { ...s, phase: 'over', cardId: null, endsAt: null };
}

// ------------------------------------------------------------------ results

/** Teams with the top score (several on a tie; none if nobody scored). */
export function leaders(teams: readonly Team[]): Team[] {
  const top = Math.max(...teams.map((t) => t.score));
  return top > 0 ? teams.filter((t) => t.score === top) : [];
}
